// 매출을 verse flavor(web2/web3) 로 쪼개는 집계. aggregateSales 와 같은 키/환불 판정을 공유해
// "합계 = web2 + web3" 가 항상 성립하도록 한다. 순수 함수, I/O 없음.

import { dayKeyOf, isRefundedSale, productKeyOf, type Sale } from './revenue-agg';

const VX_PER_USD = 100;
const toUsd = (vx: number): number => vx / VX_PER_USD;

export type SaleFlavor = 'web2' | 'web3';

/** 집계 입력 — Sale 에 소속 verse flavor 를 붙인 형태. */
export type FlavoredSale = Sale & { flavor: SaleFlavor };

export interface FlavorStat {
  count: number;
  grossUsd: number;
  netUsd: number;
  /** 환불 제외 기준 고유 결제자 수. 행 단위(상품/일자/payer) 에서도 동일 기준. */
  payers: number;
}

/** 한 축(전체/상품/일자/payer)에 대한 web2·web3 분해. */
export interface FlavorSplit {
  web2: FlavorStat;
  web3: FlavorStat;
}

/** payer 한 명의 결제를 구성하는 상품 한 줄. netUsd 내림차순으로 정렬돼 있다. */
export interface PayerProductRow {
  productId: string;
  name: string;
  count: number;
  netUsd: number;
}

export interface RevenueSplit {
  totals: FlavorSplit;
  /** key = productId(없으면 shopItemId) — aggregateSales.byProduct 와 동일. */
  byProduct: Map<string, FlavorSplit>;
  /** key = createdAt 의 UTC 날짜(YYYY-MM-DD) — aggregateSales.byDay 와 동일. */
  byDay: Map<string, FlavorSplit>;
  /** key = userUid — aggregateSales.topPayers 와 동일. */
  byPayer: Map<string, FlavorSplit>;
  /** key = userUid — 그 payer 의 순매출이 어떤 상품들로 채워졌는지(환불 제외, net 내림차순). */
  payerProducts: Map<string, PayerProductRow[]>;
  /** 실제로 데이터가 존재하는 flavor (web2, web3 순). 단일 verse 조회면 1개. */
  flavorsPresent: SaleFlavor[];
}

// 누적은 정수 VX 로만 — USD 변환은 출력 직전 1회 (revenue-agg 와 동일 정책).
interface VxStat {
  count: number;
  grossVx: number;
  netVx: number;
  payerUids: Set<string>;
}
interface VxSplit {
  web2: VxStat;
  web3: VxStat;
}

const emptyStat = (): VxStat => ({ count: 0, grossVx: 0, netVx: 0, payerUids: new Set() });
const emptySplit = (): VxSplit => ({ web2: emptyStat(), web3: emptyStat() });

function accumulate(split: VxSplit, sale: FlavoredSale, refunded: boolean): void {
  const stat = split[sale.flavor];
  stat.count++;
  stat.grossVx += sale.totalPrice;
  if (!refunded) {
    stat.netVx += sale.totalPrice;
    stat.payerUids.add(sale.userUid);
  }
}

function bump(map: Map<string, VxSplit>, key: string, sale: FlavoredSale, refunded: boolean): void {
  const split = map.get(key) ?? emptySplit();
  accumulate(split, sale, refunded);
  map.set(key, split);
}

const freezeStat = (s: VxStat): FlavorStat => ({
  count: s.count,
  grossUsd: toUsd(s.grossVx),
  netUsd: toUsd(s.netVx),
  payers: s.payerUids.size,
});

const freezeSplit = (s: VxSplit): FlavorSplit => ({
  web2: freezeStat(s.web2),
  web3: freezeStat(s.web3),
});

function freezeMap(m: Map<string, VxSplit>): Map<string, FlavorSplit> {
  const out = new Map<string, FlavorSplit>();
  for (const [k, v] of m) out.set(k, freezeSplit(v));
  return out;
}

interface VxPayerProduct {
  productId: string;
  name: string;
  count: number;
  netVx: number;
}

/** flavor 태그가 붙은 sales 를 전체/상품/일자/payer 축으로 web2·web3 분해한다. */
export function splitByFlavor(sales: FlavoredSale[]): RevenueSplit {
  const totals = emptySplit();
  const byProduct = new Map<string, VxSplit>();
  const byDay = new Map<string, VxSplit>();
  const byPayer = new Map<string, VxSplit>();
  const payerProducts = new Map<string, Map<string, VxPayerProduct>>();
  const present = new Set<SaleFlavor>();

  for (const s of sales) {
    const refunded = isRefundedSale(s);
    const productKey = productKeyOf(s);
    present.add(s.flavor);
    accumulate(totals, s, refunded);
    bump(byProduct, productKey, s, refunded);
    bump(byDay, dayKeyOf(s), s, refunded);
    bump(byPayer, s.userUid, s, refunded);

    // 구성 품목은 순매출 기준 — 환불 건은 "무엇에 썼는지"에서 제외한다.
    if (refunded) continue;
    const rows = payerProducts.get(s.userUid) ?? new Map<string, VxPayerProduct>();
    const row = rows.get(productKey) ?? {
      productId: productKey,
      name: s.item?.name ?? productKey,
      count: 0,
      netVx: 0,
    };
    row.count++;
    row.netVx += s.totalPrice;
    rows.set(productKey, row);
    payerProducts.set(s.userUid, rows);
  }

  const frozenPayerProducts = new Map<string, PayerProductRow[]>();
  for (const [uid, rows] of payerProducts) {
    frozenPayerProducts.set(
      uid,
      [...rows.values()]
        .sort((a, b) => b.netVx - a.netVx || a.productId.localeCompare(b.productId))
        .map((r) => ({
          productId: r.productId,
          name: r.name,
          count: r.count,
          netUsd: toUsd(r.netVx),
        }))
    );
  }

  return {
    totals: freezeSplit(totals),
    byProduct: freezeMap(byProduct),
    byDay: freezeMap(byDay),
    byPayer: freezeMap(byPayer),
    payerProducts: frozenPayerProducts,
    flavorsPresent: (['web2', 'web3'] as const).filter((f) => present.has(f)),
  };
}

const ZERO_STAT: FlavorStat = { count: 0, grossUsd: 0, netUsd: 0, payers: 0 };
const ZERO_SPLIT: FlavorSplit = { web2: ZERO_STAT, web3: ZERO_STAT };

/** 행 조인용 — 키가 없으면 0 분해를 돌려준다(테이블 렌더에서 undefined 분기 제거). */
export function splitOf(map: Map<string, FlavorSplit>, key: string): FlavorSplit {
  return map.get(key) ?? ZERO_SPLIT;
}
